import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChartLine } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { REVENUE_CHART_CARD_ICON_COLOR } from "../revenue/constants";
import { SegmentPills } from "../shared/SegmentPills";
import type { SegmentPillOption } from "../shared/SegmentPills";
import {
  AnalyticsAreaFillDefs,
  analyticsAreaFillUrl,
  useAnalyticsChartInstanceId,
} from "../shared/chartAreaFill";
import { formatAnalyticsCurrency } from "../shared/analyticsFormatting";
import type { OverviewAreaChartPoint } from "./overviewAreaChartMappers";

export type OverviewMetricKey =
  | "revenue"
  | "offerConversion"
  | "avgRating"
  | "ordersForStay";

interface OverviewMetricDefinition {
  key: OverviewMetricKey;
  label: string;
  color: string;
}

function getXAxisInterval(pointCount: number): number | "preserveStartEnd" {
  if (pointCount <= 14) {
    return 0;
  }
  if (pointCount <= 31) {
    return 4;
  }
  return Math.max(1, Math.floor(pointCount / 8));
}

function getNiceAxisMaximum(maxValue: number): number {
  if (maxValue <= 0) {
    return 1;
  }

  const magnitude = 10 ** Math.floor(Math.log10(maxValue));
  const normalized = maxValue / magnitude;
  let multiplier = 10;

  if (normalized <= 1) {
    multiplier = 1;
  } else if (normalized <= 2) {
    multiplier = 2;
  } else if (normalized <= 5) {
    multiplier = 5;
  }

  return multiplier * magnitude;
}

function formatMetricValue(metric: OverviewMetricKey, value: number): string {
  if (metric === "revenue") {
    return formatAnalyticsCurrency(value, {
      maximumFractionDigits: 2,
      symbol: "€",
      symbolPosition: "suffix",
    });
  }

  const formatted = value.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });

  return metric === "offerConversion" ? `${formatted}%` : formatted;
}

interface OverviewAreaChartProps {
  data: OverviewAreaChartPoint[];
  isLoading?: boolean;
  isError?: boolean;
}

export const OverviewAreaChart: React.FC<OverviewAreaChartProps> = ({
  data,
  isLoading = false,
  isError = false,
}) => {
  const { t } = useTranslation();
  const [activeMetric, setActiveMetric] =
    useState<OverviewMetricKey>("revenue");
  const chartInstanceId = useAnalyticsChartInstanceId();
  const metricDefinitions = useMemo<OverviewMetricDefinition[]>(
    () => [
      {
        key: "revenue",
        label: t("analytics.overview.kpi.serviceRevenue", "Service revenue"),
        color: "#A78BFA",
      },
      {
        key: "offerConversion",
        label: t(
          "analytics.overview.kpi.conversionOfOffers",
          "Conversion of offers",
        ),
        color: "#CAC19F",
      },
      {
        key: "avgRating",
        label: t(
          "analytics.overview.kpi.avgGuestRatingForStay",
          "Avg. Guest Rating for Stay",
        ),
        color: "#9B90DA",
      },
      {
        key: "ordersForStay",
        label: t("analytics.overview.kpi.ordersForStay", "Orders for stay"),
        color: "#F97316",
      },
    ],
    [t],
  );
  const activeDefinition =
    metricDefinitions.find((metric) => metric.key === activeMetric) ??
    metricDefinitions[0];
  const metricOptions = useMemo<SegmentPillOption<OverviewMetricKey>[]>(
    () =>
      metricDefinitions.map((metric) => ({
        value: metric.key,
        label: metric.label,
      })),
    [metricDefinitions],
  );
  const chartConfig = useMemo<ChartConfig>(() => {
    return metricDefinitions.reduce<ChartConfig>((config, metric) => {
      config[metric.key] = {
        label: metric.label,
        color: metric.color,
      };
      return config;
    }, {});
  }, [metricDefinitions]);
  const xAxisInterval = useMemo(
    () => getXAxisInterval(data.length),
    [data.length],
  );
  const activeValues = useMemo(
    () =>
      data
        .map((point) => point[activeMetric])
        .filter((value): value is number => value !== null),
    [activeMetric, data],
  );
  const yAxisMax = useMemo(() => {
    if (activeMetric === "offerConversion") {
      return 100;
    }
    if (activeMetric === "avgRating") {
      return 5;
    }

    return getNiceAxisMaximum(Math.max(0, ...activeValues));
  }, [activeMetric, activeValues]);

  const chartBody = (() => {
    if (isError) {
      return (
        <p className="text-muted-foreground flex h-[320px] items-center justify-center text-sm">
          {t(
            "analytics.overview.chart.loadError",
            "Failed to load overview trend data.",
          )}
        </p>
      );
    }

    if (!isLoading && (data.length === 0 || activeValues.length === 0)) {
      return (
        <div className="flex flex-col gap-3">
          <SegmentPills
            value={activeMetric}
            options={metricOptions}
            onChange={setActiveMetric}
            ariaLabel={t(
              "analytics.overview.chart.metricSelector",
              "Overview trend metric",
            )}
          />
          <p className="text-muted-foreground flex h-[284px] items-center justify-center text-sm">
            {t(
              "analytics.overview.chart.noData",
              "No overview trend data for this period.",
            )}
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3">
        <SegmentPills
          value={activeMetric}
          options={metricOptions}
          onChange={setActiveMetric}
          ariaLabel={t(
            "analytics.overview.chart.metricSelector",
            "Overview trend metric",
          )}
        />
        <ChartContainer
          config={chartConfig}
          className={cn(
            "!aspect-auto h-[320px] w-full transition-opacity",
            isLoading && "pointer-events-none opacity-50",
          )}
        >
          <AreaChart
            data={data}
            margin={{ top: 8, right: 8, left: 4, bottom: 0 }}
          >
            <defs>
              <AnalyticsAreaFillDefs
                seriesKeys={metricDefinitions.map((metric) => metric.key)}
                chartInstanceId={chartInstanceId}
              />
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval={xAxisInterval}
              className="text-muted-foreground text-xs"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={56}
              domain={[0, yAxisMax]}
              tickFormatter={(value) =>
                formatMetricValue(activeMetric, Number(value))
              }
              className="text-muted-foreground text-xs"
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  indicator="dot"
                  formatter={(value) => [
                    formatMetricValue(activeMetric, Number(value)),
                    activeDefinition.label,
                  ]}
                />
              }
            />
            <Area
              type="monotone"
              dataKey={activeMetric}
              stroke={`var(--color-${activeMetric})`}
              strokeWidth={2}
              connectNulls
              fill={analyticsAreaFillUrl(activeMetric, chartInstanceId)}
            />
          </AreaChart>
        </ChartContainer>
      </div>
    );
  })();

  return (
    <RevenueChartCard
      title={t("analytics.overview.title", "Overview")}
      icon={<ChartLine />}
      iconColor={REVENUE_CHART_CARD_ICON_COLOR}
    >
      {chartBody}
    </RevenueChartCard>
  );
};
